import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Check, X, Star, Globe, Repeat, Shield, Clock } from 'lucide-react';
import SEO from '@/components/SEO';
import Icon from '@/components/Icon';
import {
  comparison,
  serviceQuality,
  customerStats,
  customerLogos,
  featureHighlights,
  migration,
  faqs,
  site,
} from '@/data/content';

function CompareValue({ value }: { value: boolean | string }) {
  if (value === true) return <Check className="mx-auto h-5 w-5 text-green-600" />;
  if (value === false) return <X className="mx-auto h-5 w-5 text-slate-300" />;
  if (value === 'partial') return <span className="inline-flex rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-bold text-amber-700">Partial</span>;
  if (value === 'add-on') return <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-bold text-slate-700">Add-on</span>;
  return <span className="font-bold text-slate-700">{value}</span>;
}

const whySwitch = [
  { icon: 'Wallet', title: 'Lower cost', text: 'ZuriHub starts at $25 per agent and replaces live chat, tickets, phone, knowledge base, and analytics tools.' },
  { icon: 'Zap', title: 'AI included', text: 'Zyra AI writes replies, summarizes threads, and deflects FAQs on every plan that needs it.' },
  { icon: 'Headphones', title: 'Built-in voice', text: 'Make, receive, and transcribe AI voice calls without buying a separate phone stack.' },
  { icon: 'Globe', title: 'Global ready', text: 'Run support for Africa, Europe, North America, and Asia from one workspace with 99.99% uptime.' },
];

export default function WhyChoose() {
  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Why Choose ZuriHub',
      description: 'See why teams choose ZuriHub over Intercom, Zendesk, and others.',
      url: `${site.baseUrl}/why-zuri`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.slice(0, 4).map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    },
  ];

  return (
    <>
      <SEO
        title="Why Choose ZuriHub"
        description="Unified inbox, AI voice, $25/seat pricing, and better value than Intercom, Zendesk, and Front."
        path="/why-zuri"
        jsonLd={jsonLd}
      />

      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-24 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/30 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-5xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-4 py-1.5 text-sm font-semibold text-slate-300 backdrop-blur">
            <Star className="h-4 w-4 text-yellow-400" /> Why ZuriHub
          </span>
          <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            More power. <span className="gradient-text">Less cost.</span> Zero tool-hopping.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate-400">
            Teams switch to ZuriHub to unify channels, add AI voice and live chat, and stop paying for five tools. See the comparison and decide for yourself.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <a
              href={site.workspaceUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-base font-bold text-slate-900 transition hover:bg-slate-100"
            >
              Start free trial <ArrowUpRight className="h-4 w-4" />
            </a>
            <Link
              to="/features"
              className="inline-flex items-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-8 py-3.5 text-base font-semibold text-white transition hover:bg-slate-800"
            >
              Explore features <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-50" aria-labelledby="comparison-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="comparison-heading" className="section-title">How ZuriHub compares</h2>
            <p className="section-subtitle mx-auto">
              The features that matter most, side by side with the most common alternatives.
            </p>
          </div>
          <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
            <div className="grid grid-cols-4 gap-4 border-b border-slate-200 bg-slate-100/50 p-4 text-sm font-bold text-slate-900 sm:grid-cols-4 sm:p-6">
              <span>Feature</span>
              <span className="text-center">ZuriHub</span>
              <span className="text-center">Intercom</span>
              <span className="text-center">Zendesk</span>
            </div>
            {comparison.rows.map((row) => (
              <div
                key={row.feature}
                className="grid grid-cols-4 gap-4 border-b border-slate-100 p-4 text-sm last:border-0 sm:p-6"
              >
                <span className="font-semibold text-slate-900">{row.feature}</span>
                <span className="text-center"><CompareValue value={row.zurihub} /></span>
                <span className="text-center"><CompareValue value={row.intercom} /></span>
                <span className="text-center"><CompareValue value={row.zendesk} /></span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white" aria-labelledby="why-switch-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="why-switch-heading" className="section-title">Why teams switch to ZuriHub</h2>
            <p className="section-subtitle mx-auto">
              The most common reasons teams move from Intercom, Zendesk, and Freshdesk.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {whySwitch.map((w) => (
              <div
                key={w.title}
                className="rounded-3xl border border-slate-100 bg-slate-50 p-8 transition hover:-translate-y-1 hover:shadow-xl"
              >
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-100 to-brand-50 text-brand-700 shadow-inner">
                  <Icon name={w.icon} className="h-7 w-7" />
                </div>
                <h3 className="text-xl font-extrabold text-slate-900">{w.title}</h3>
                <p className="mt-3 text-slate-600">{w.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-900 text-white" aria-labelledby="quality-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="quality-heading" className="section-title text-white">Built for scale and trust</h2>
            <p className="section-subtitle mx-auto text-slate-300">
              Performance and compliance teams can rely on from day one.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {serviceQuality.map((q) => (
              <div
                key={q.label}
                className="rounded-3xl border border-slate-700 bg-slate-800/50 p-8 text-center"
              >
                <p className="text-4xl font-extrabold text-brand-400">{q.metric}</p>
                <p className="mt-2 font-bold text-slate-200">{q.label}</p>
                <p className="mt-2 text-sm text-slate-400">{q.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-50" aria-labelledby="global-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <h2 id="global-heading" className="section-title">Global by default</h2>
              <p className="section-subtitle">
                ZuriHub runs on a global edge network with data centers across Africa, Europe, North America, and Asia-Pacific.
              </p>
              <div className="mt-8 space-y-4">
                <div className="flex items-center gap-3 text-slate-700">
                  <Globe className="h-5 w-5 text-brand-600" />
                  <span className="font-semibold">Multi-region availability</span>
                </div>
                <div className="flex items-center gap-3 text-slate-700">
                  <Clock className="h-5 w-5 text-brand-600" />
                  <span className="font-semibold">99.99% uptime SLA on Enterprise</span>
                </div>
                <div className="flex items-center gap-3 text-slate-700">
                  <Shield className="h-5 w-5 text-brand-600" />
                  <span className="font-semibold">GDPR and SOC 2 ready</span>
                </div>
                <div className="flex items-center gap-3 text-slate-700">
                  <Repeat className="h-5 w-5 text-brand-600" />
                  <span className="font-semibold">Automatic scaling as you grow</span>
                </div>
              </div>
            </div>
            <div className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
              <h3 className="text-xl font-bold text-slate-900">Trusted by teams in</h3>
              <ul className="mt-6 space-y-3">
                {customerLogos.map((logo) => (
                  <li key={logo} className="flex items-center gap-3 text-sm font-semibold text-slate-700">
                    <Check className="h-4 w-4 text-green-600" />
                    {logo}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white" aria-labelledby="migration-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="migration-heading" className="section-title">Migrate with zero stress</h2>
            <p className="section-subtitle mx-auto">
              {migration.subtitle}
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2">
            {migration.benefits.map((b) => (
              <div
                key={b}
                className="flex items-start gap-4 rounded-2xl border border-slate-100 bg-slate-50 p-6"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-700">
                  <Check className="h-4 w-4" />
                </div>
                <p className="text-lg font-semibold text-slate-900">{b}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-slate-600">
            Free migration from {migration.from.join(', ')} on Pro and Enterprise.
          </p>
        </div>
      </section>

      <section className="py-24 bg-slate-900 text-white" aria-labelledby="stats-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {customerStats.map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="text-5xl font-extrabold text-brand-400">{stat.value}</p>
                <p className="mt-2 text-lg font-medium text-slate-300">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-slate-50" aria-labelledby="highlights-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-2">
            {featureHighlights.map((h) => (
              <div
                key={h.id}
                className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm"
              >
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-100 to-brand-50 text-brand-700 shadow-inner">
                  <Icon name={h.icon} className="h-7 w-7" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900">{h.title}</h2>
                <p className="mt-3 text-slate-600">{h.summary}</p>
                <ul className="mt-6 space-y-3">
                  {h.items.map((item) => (
                    <li key={item} className="flex items-start gap-3 text-sm font-semibold text-slate-700">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="faq-heading" className="section-title text-center">Common questions</h2>
          <div className="mt-10 space-y-4">
            {faqs.slice(0, 4).map((f) => (
              <div
                key={f.question}
                className="rounded-2xl border border-slate-100 bg-slate-50 p-6"
              >
                <h3 className="font-bold text-slate-900">{f.question}</h3>
                <p className="mt-2 text-slate-600">{f.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl rounded-3xl bg-slate-900 px-6 py-16 text-center text-white sm:px-12">
          <h2 className="text-3xl font-extrabold sm:text-4xl">See why teams are switching</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-300">
            Start a 14-day free trial and compare ZuriHub to your current stack.
          </p>
          <a
            href={site.workspaceUrl}
            target="_blank"
            rel="noreferrer"
            className="gradient-cta mt-8 inline-flex items-center gap-2"
          >
            Start free trial <ArrowUpRight className="h-4 w-4" />
          </a>
        </div>
      </section>
    </>
  );
}
